import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import CommonMessages from '~/core/commonMessages';
import { PlayerStatNames, RouteNavMatch } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import {
    FormatPlayerPosition,
    getPlayerHref,
    parseValueFromLocalizedAttribute,
    useGoalDescriptors,
} from '~/core/utils';
import GoalDescriptors from '~/views/components/goalDescriptors/GoalDescriptors';
import VideoClipPlayer from '~/views/components/videoClipPlayer/VideoClipPlayer';

import { GoalChipProps, ScoresGame, TeamLeaderChipProps } from '../../../../types';

import {
    BoldedTeam,
    ChevronLeft,
    ChevronRight,
    GoalScoreAndTimeContainer,
    GoalSubtextContainer,
    InfoContainer,
    PlayerChipContainer,
    PlayerChips,
    PlayerHeadshotContainer,
    PlayerInfoContainer,
    PlayerInfoWrapper,
    PlayerItemContainer,
    StatsToggle,
    TeamLeaderCount,
    TeamLeaderCountCategory,
    TeamLeaderCountContainer,
    TeamLeaderName,
    TeamLeaderTeam,
    ToggleContainer,
    Toggles,
} from './styles';

type PlayerCarouselProps = {
    game: ScoresGame;
};

const formatGameScore = (
    awayTeamName: string,
    homeTeamName: string,
    awayTeamScore: number,
    homeTeamScore: number,
    homeTeamScored: boolean
): ReactNode => {
    return (
        <FormattedMessage
            description="Game score at the time of a goal, ie PHI 3 - ARI 1"
            defaultMessage="<b>{awayTeamName} {awayTeamScore}</b> - <c>{homeTeamName} {homeTeamScore}</c>"
            values={{
                b: (text) => <BoldedTeam $isScoringTeam={!homeTeamScored}>{text}</BoldedTeam>,
                c: (text) => <BoldedTeam $isScoringTeam={homeTeamScored}>{text}</BoldedTeam>,
                awayTeamName,
                awayTeamScore,
                homeTeamName,
                homeTeamScore,
            }}
        />
    );
};

const GoalChip: FC<GoalChipProps> = ({ goal, game, isWebview }) => {
    const theme = useTheme();
    const intl = useIntl();

    const { homeTeam, awayTeam } = game;
    const { getPeriodDisplayFromDescriptor } = CoreUtils;

    const goalDescriptors = useGoalDescriptors(goal.strength, goal.goalModifier);

    let goalSubtext: string;

    if (goal.periodDescriptor.periodType === ShortCodeEnums.PeriodType.shootout) {
        goalSubtext = intl.formatMessage({
            description: 'Indicates that a goal was the goal which ended, and thus won, the shootout period.',
            defaultMessage: 'Shootout Winner',
        });
    } else if (goal.assists.length > 0) {
        goalSubtext = intl.formatList(
            goal.assists.map((assist) => `${parseValueFromLocalizedAttribute(assist.name)} (${assist.assistsToDate})`),
            { type: 'unit' }
        );
    } else {
        goalSubtext = intl.formatMessage(CommonMessages.statsDescriptions.long.unassisted);
    }

    return (
        <PlayerItemContainer className="goal-chip" $isTeamLeaderCarousel={false}>
            <PlayerHeadshotContainer href={getPlayerHref(goal.playerId, isWebview)} rel="noreferrer">
                <Headshot
                    type={headshotTypes.SKATER}
                    urlOverride={goal.mugshot}
                    size={50}
                    backgroundColor={
                        goal.teamAbbrev ? theme.Team[goal.teamAbbrev]?.gamecenter : theme.Surface.secondary
                    }
                />
            </PlayerHeadshotContainer>
            <InfoContainer>
                <PlayerInfoWrapper>
                    <div>
                        {parseValueFromLocalizedAttribute(goal.firstName)}&nbsp;
                        {parseValueFromLocalizedAttribute(goal.lastName)}
                    </div>
                    {goal.periodDescriptor.periodType !== ShortCodeEnums.PeriodType.shootout && (
                        <div>({goal.goalsToDate})</div>
                    )}
                    <GoalDescriptors goalDescriptors={goalDescriptors} />
                </PlayerInfoWrapper>
                <GoalSubtextContainer title={goalSubtext}>{goalSubtext}</GoalSubtextContainer>
                <GoalScoreAndTimeContainer>
                    {formatGameScore(
                        awayTeam.abbrev,
                        homeTeam.abbrev,
                        goal.awayScore,
                        goal.homeScore,
                        goal.teamAbbrev === homeTeam.abbrev
                    )}
                    (
                    {goal.periodDescriptor.periodType !== ShortCodeEnums.PeriodType.shootout ? (
                        <FormattedMessage
                            description="This represents the period and time a goal was scored. Ex. 1st 12:05. (Period will already be formatted with proper ordinality 1 will be 1st)"
                            defaultMessage="{period} - {timeInPeriod}"
                            values={{
                                period: getPeriodDisplayFromDescriptor(intl, goal.periodDescriptor, game.gameType),
                                timeInPeriod: goal.timeInPeriod,
                            }}
                        />
                    ) : (
                        getPeriodDisplayFromDescriptor(intl, goal.periodDescriptor, game.gameType)
                    )}
                    )
                </GoalScoreAndTimeContainer>
            </InfoContainer>
            <VideoClipPlayer
                homeTeam={homeTeam}
                awayTeam={awayTeam}
                goal={goal}
                gameState={game.gameState}
                pageName={RouteNavMatch.SCORES}
            />
        </PlayerItemContainer>
    );
};

const TeamLeaderChip: FC<TeamLeaderChipProps> = ({ player, isWebview }) => {
    const theme = useTheme();

    return (
        <PlayerItemContainer className="team-leader-chip" $isTeamLeaderCarousel>
            <PlayerInfoContainer>
                <PlayerHeadshotContainer href={getPlayerHref(player.id, isWebview)} rel="noreferrer">
                    <Headshot
                        type={player.category === PlayerStatNames.wins ? headshotTypes.GOALIE : headshotTypes.SKATER}
                        urlOverride={player.headshot}
                        size={48}
                        backgroundColor={
                            player.teamAbbrev ? theme.Team[player.teamAbbrev]?.gamecenter : theme.Surface.secondary
                        }
                    />
                </PlayerHeadshotContainer>
                <InfoContainer>
                    <TeamLeaderName $isLastName={false}>
                        {parseValueFromLocalizedAttribute(player.firstName)}
                    </TeamLeaderName>
                    <TeamLeaderName $isLastName>{parseValueFromLocalizedAttribute(player.lastName)}</TeamLeaderName>
                    <TeamLeaderTeam>
                        {player.teamAbbrev}
                        &nbsp;&bull;&nbsp;
                        <FormattedMessage
                            {...CommonMessages.playerInfoDescriptions.long.number}
                            values={{ sweaterNumber: player.sweaterNumber }}
                        />
                        &nbsp;&bull;&nbsp;
                        <FormatPlayerPosition position={player.position} />
                    </TeamLeaderTeam>
                </InfoContainer>
            </PlayerInfoContainer>
            <TeamLeaderCountContainer>
                <TeamLeaderCount>{player.value}</TeamLeaderCount>
                <TeamLeaderCountCategory>
                    {player.category && (
                        <CoreUtils.SafeFormattedMessage
                            message={
                                CommonMessages.statsDescriptions[player.value > 1 ? 'long' : 'short'][
                                    PlayerStatNames[player.category]
                                ]
                            }
                        />
                    )}
                </TeamLeaderCountCategory>
            </TeamLeaderCountContainer>
        </PlayerItemContainer>
    );
};

const PlayerCarousel: FC<PlayerCarouselProps> = ({ game }) => {
    const isWebview = useIsWebview();

    const [selectedIndex, setSelectedIndex] = useState(0);

    const chipsRef = useRef<HTMLDivElement>(null);
    const chipsContainerRef = useRef<HTMLDivElement>(null);
    const playerLength = game.goals?.length || game.teamLeaders?.length || 0;
    const isLiveGame = CoreUtils.isGameLive(game.gameState);

    const onScrollChange = useCallback(() => {
        if (chipsRef.current && chipsContainerRef.current && playerLength) {
            const containerScrollWidth = chipsContainerRef.current.scrollWidth;
            const containerOffsetWidth = chipsContainerRef.current.offsetWidth;
            const containerLeftStart = chipsContainerRef.current.getBoundingClientRect().left;
            const left = containerLeftStart - chipsRef.current.getBoundingClientRect().left;
            const scrollWidth = chipsRef.current.scrollWidth / playerLength;
            const scrollIndex = Math.abs(Math.ceil(left / scrollWidth));

            containerScrollWidth - containerOffsetWidth === left
                ? setSelectedIndex(playerLength - 1)
                : setSelectedIndex(scrollIndex);
        }
    }, [playerLength]);

    useScroll({
        onScrollChanged: onScrollChange,
        scrollRef: chipsContainerRef,
    });

    const scrollToIndex = (chipIndex: number) => {
        if (chipsContainerRef.current && chipsRef.current && playerLength) {
            const playerChipsNode = chipsContainerRef.current;
            const chipWidth = chipsRef.current.scrollWidth / playerLength;

            const center = playerChipsNode.offsetWidth / 2;
            const offCenter = center - chipWidth / 2;

            playerChipsNode &&
                playerChipsNode.scrollTo({
                    left: chipWidth * chipIndex - offCenter,
                    behavior: 'smooth',
                });
        }
    };

    const getChips = (): Array<ReactNode> => {
        if (game.goals && game.goals.length > 0) {
            return game.goals.map((goal, i) => <GoalChip key={i} goal={goal} game={game} isWebview={isWebview} />);
        } else if (game.teamLeaders) {
            return game.teamLeaders.map((player, i) => (
                <TeamLeaderChip key={i} player={player} isWebview={isWebview} />
            ));
        }

        return [];
    };

    const getToggles = (): Array<ReactNode> => {
        if (game.goals && game.goals.length > 0) {
            return game.goals.map((g, i) => (
                <StatsToggle key={i} onClick={() => scrollToIndex(i)} $selected={i === selectedIndex} />
            ));
        } else if (game.teamLeaders) {
            return game.teamLeaders.map((p, i) => (
                <StatsToggle key={i} onClick={() => scrollToIndex(i)} $selected={i === selectedIndex} />
            ));
        }

        return [];
    };

    const chips = getChips();

    return playerLength > 0 ? (
        <>
            <PlayerChipContainer ref={chipsContainerRef}>
                <PlayerChips ref={chipsRef}>
                    {isLiveGame && game.goals && game.goals.length > 0 ? chips.reverse() : chips}
                </PlayerChips>
            </PlayerChipContainer>
            <ToggleContainer>
                <ChevronLeft
                    $disabled={selectedIndex === 0}
                    onClick={() => selectedIndex !== 0 && scrollToIndex(selectedIndex - 1)}
                />
                <Toggles>{getToggles()}</Toggles>
                <ChevronRight
                    $disabled={selectedIndex === (playerLength && playerLength - 1)}
                    onClick={() =>
                        selectedIndex !== (playerLength && playerLength - 1) && scrollToIndex(selectedIndex + 1)
                    }
                />
            </ToggleContainer>
        </>
    ) : null;
};

export default PlayerCarousel;
